import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { Separator } from "../components/ui/separator";

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen" data-testid="terms-conditions-page">
      <Navigation />
      
      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">Terms & Conditions</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-medium mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to Art By Arawol. These Terms and Conditions ("Terms") govern your use of our website 
                and the purchase of products from us. By accessing our website or making a purchase, you agree 
                to be bound by these Terms.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Art By Arawol operates under the laws of New South Wales, Australia, and complies with the 
                Australian Consumer Law (ACL) as set out in Schedule 2 of the Competition and Consumer Act 2010 (Cth).
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">2. Australian Consumer Law Guarantees</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our goods come with guarantees that cannot be excluded under the Australian Consumer Law. 
                You are entitled to a replacement or refund for a major failure and compensation for any 
                other reasonably foreseeable loss or damage. You are also entitled to have the goods repaired 
                or replaced if the goods fail to be of acceptable quality and the failure does not amount to 
                a major failure.
              </p>
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Nothing in these Terms is intended to limit or exclude any guarantees, 
                  warranties, representations or conditions implied or imposed by law, including the Australian 
                  Consumer Law, which by law may not be limited or excluded.
                </p>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">3. Products</h2>
              <h3 className="text-xl font-medium mb-2 mt-6">3.1 Product Descriptions</h3>
              <p className="text-muted-foreground leading-relaxed">
                We endeavor to display our products as accurately as possible. However, colors may vary slightly 
                due to screen settings and lighting conditions in photographs. All artwork dimensions are 
                approximate and may vary slightly.
              </p>
              
              <h3 className="text-xl font-medium mb-2 mt-6">3.2 Authenticity</h3>
              <p className="text-muted-foreground leading-relaxed">
                All artworks sold through Art By Arawol are authentic works by the stated artist. Each purchase 
                includes a Certificate of Authenticity. Our artworks are created by Aboriginal artist Colin 
                Isaacs, a Gadigal and Bidjigal Elder.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">3.3 Limited Editions</h3>
              <p className="text-muted-foreground leading-relaxed">
                Limited edition prints are numbered and strictly limited to the stated quantity. Once sold out, 
                no additional prints will be produced in that edition.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">4. Pricing and Payment</h2>
              <h3 className="text-xl font-medium mb-2 mt-6">4.1 Prices</h3>
              <p className="text-muted-foreground leading-relaxed">
                All prices are displayed in Australian Dollars (AUD) and include GST where applicable. 
                We reserve the right to change prices at any time without notice, but this will not affect 
                orders already placed.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">4.2 Payment Methods</h3>
              <p className="text-muted-foreground leading-relaxed">
                We accept payment via credit/debit card (through Stripe), PayPal, and PayPal Pay Later options. 
                All payment processing is handled by secure, PCI-DSS compliant third-party processors.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">4.3 Wholesale Pricing</h3>
              <p className="text-muted-foreground leading-relaxed">
                Approved wholesale customers may be eligible for tiered pricing discounts. Wholesale terms are 
                subject to a separate agreement and approval process.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">5. Orders and Acceptance</h2>
              <p className="text-muted-foreground leading-relaxed">
                Placing an order constitutes an offer to purchase. We reserve the right to accept or decline 
                any order. Order acceptance occurs when we send you a shipping confirmation. Until acceptance, 
                we may cancel your order and refund any payment made.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We reserve the right to limit quantities, refuse service, or cancel orders at our discretion, 
                including where we suspect fraudulent activity.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">6. Auctions</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Silent auctions are subject to the following additional terms:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All bids are binding offers to purchase</li>
                <li>The highest bid at auction close wins, subject to any reserve price being met</li>
                <li>Anti-sniping rules may extend the auction if bids are placed near closing time</li>
                <li>Winners will be notified by email with payment instructions</li>
                <li>Payment must be completed within 48 hours of auction close</li>
                <li>Failure to complete payment may result in the item being offered to the next highest bidder</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">7. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All content on this website, including images, text, designs, and artwork reproductions, is 
                protected by copyright and intellectual property laws. Purchasing artwork grants you ownership 
                of the physical piece but does not transfer copyright or reproduction rights.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You may not reproduce, distribute, or create derivative works from our artwork without express 
                written permission from Art By Arawol and the artist.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">8. User Accounts</h2>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for maintaining the confidentiality of your account credentials and for all 
                activities under your account. You must notify us immediately of any unauthorized use.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">9. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law and subject to consumer guarantees under the Australian 
                Consumer Law, our total liability for any claim arising from your use of our website or purchase 
                of products is limited to the amount you paid for the relevant product.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We are not liable for any indirect, incidental, special, consequential, or punitive damages.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">10. Indemnification</h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to indemnify and hold harmless Art By Arawol, its officers, directors, employees, 
                and agents from any claims, losses, or damages arising from your breach of these Terms or 
                misuse of our website.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">11. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by the laws of New South Wales, Australia. Any disputes will be 
                subject to the exclusive jurisdiction of the courts of New South Wales, without prejudice 
                to your rights under the Australian Consumer Law.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">12. Dispute Resolution</h2>
              <p className="text-muted-foreground leading-relaxed">
                We aim to resolve any disputes amicably. If you have a complaint, please contact us first. 
                If we cannot resolve the issue, you may seek assistance from NSW Fair Trading or take action 
                in an appropriate tribunal or court.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">13. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may modify these Terms at any time. Changes will be effective immediately upon posting 
                to our website. Continued use of our website after changes constitutes acceptance of the 
                modified Terms.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">14. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                For questions about these Terms, please contact us:
              </p>
              <div className="mt-4 p-4 bg-card rounded-xl border border-border">
                <p className="text-muted-foreground">
                  <strong>Art By Arawol</strong><br />
                  Email: legal@arawol.art<br />
                  New South Wales, Australia
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsConditionsPage;
